import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams, Outlet } from 'react-router-dom';

import { useAppDispatch, useTranslate } from '@src/core/hooks';

import { setupLocale } from '@src/stores/userPreferences';
import { setupAuth } from '@src/features/customers';
import { loadAppInfo, setPublicKey } from '@src/stores/app';
import { directLinkStateSelector, loadDirectLink } from '@src/stores/directLink';

import SpinnerBar from '@src/components/reusable/spinner/SpinnerBar';
import Footer from '@src/components/DirectLink/Footer';
import Header from '@src/components/DirectLink/Header';

import { Wrapper, LoadingWrapper } from './styles';
import Error from '@src/components/DirectLink/Error';
import { getSessionId } from '@src/common';
import { setGlobalParameter } from '@src/stores/globalParameters';
import { CustomerAccessTokenParameter } from '@src/core/parameters';

type RouteParams = {
  linkCode: string;
  apiKey: string;
};

export const Base = () => {
  const { apiKey, linkCode } = useParams<RouteParams>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const { directLink, status, error } = useSelector(directLinkStateSelector);

  const isLoading = status === 'loading';

  const getDocumentLanguage = () => {
    const language = document.documentElement.lang;

    return language && language !== '' ? language : 'pt-br';
  };

  // The token arrives in the query string; drop it from the URL right away so it doesn't leak
  // through shared links, referers or analytics. Like the DirectLink widget, it isn't persisted.
  const consumeCustomerAccessToken = () => {
    const token = searchParams.get(CustomerAccessTokenParameter.name);
    if (!token) {
      return undefined;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete(CustomerAccessTokenParameter.name);
    setSearchParams(nextSearchParams, { replace: true });

    return token;
  };

  const initializeStore = (key: string, language: string) => {
    const customerAccessToken = consumeCustomerAccessToken();

    dispatch(setGlobalParameter('sessionId', getSessionId()));
    dispatch(setupLocale(language));
    dispatch(setPublicKey(key));

    if (customerAccessToken) {
      dispatch(setGlobalParameter(CustomerAccessTokenParameter.name, customerAccessToken));
      dispatch(setupAuth(false, customerAccessToken));
    } else {
      dispatch(setupAuth());
    }
    // just a delay to avoid too much parallel requests
    setTimeout(() => {
      dispatch(loadAppInfo());
    }, 200);
  };

  const getTitle = () => {
    if (directLink?.type === 'cart') {
      return i18n('direct_link_sale_title');
    }

    if (directLink?.type === 'payment') {
      return i18n('direct_link_payment_title');
    }

    return '';
  };

  useEffect(() => {
    if (linkCode) {
      const language = getDocumentLanguage();

      initializeStore(apiKey || '', language);

      dispatch(loadDirectLink(linkCode));
    }
  }, []);

  return (
    <>
      <Wrapper>
        <Header title={getTitle()} app={directLink?.app} />
        {isLoading ? (
          <LoadingWrapper>
            <SpinnerBar />
          </LoadingWrapper>
        ) : error ? (
          <Error>{error}</Error>
        ) : (
          <Outlet />
        )}
        <Footer />
      </Wrapper>
    </>
  );
};

export default Base;
